import React from 'react';
import { Loader2, Lock, CreditCard, Activity } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';

const formatCurrency = (cents) => {
  if (cents >= 100) return `$${(cents / 100).toFixed(2)}`;
  return `${cents}¢`;
};

export const OrderForm = ({
  activeTab,
  setActiveTab,
  outcome,
  orderType,
  setOrderType,
  price,
  setPrice,
  quantity,
  setQuantity,
  effectivePrice,
  bestAsk,
  bestBid,
  totalCost,
  potentialPayout,
  potentialProfit,
  isAuthenticated,
  walletBalance,
  userPositions,
  error,
  showSubscribePrompt,
  onCloseSubscribePrompt,
  onShowSubscribe,
  submitting,
  onPlaceOrder,
}) => {
  const noLiquidity = (activeTab === 'buy' && bestAsk >= 100) || (activeTab === 'sell' && bestBid <= 0);

  return (
    <>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full bg-[#1e2a45] p-1 rounded-lg">
          <TabsTrigger
            value="buy"
            className="flex-1 rounded-md text-gray-300 data-[state=active]:bg-green-600 data-[state=active]:text-white"
          >
            BUY
          </TabsTrigger>
          <TabsTrigger
            value="sell"
            className="flex-1 rounded-md text-gray-300 data-[state=active]:bg-red-600 data-[state=active]:text-white"
          >
            SELL
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Order Type Toggle */}
      <div className="flex gap-2 p-1 bg-[#1e2a45] rounded-lg">
        <button
          onClick={() => setOrderType('limit')}
          data-testid="limit-order-btn"
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
            orderType === 'limit'
              ? 'bg-[#3B82F6] text-white shadow-lg'
              : 'text-gray-400 hover:text-white hover:bg-[#2d3a56]'
          }`}
        >
          Limit Order
        </button>
        <button
          onClick={() => setOrderType('market')}
          data-testid="market-order-btn"
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
            orderType === 'market'
              ? 'bg-[#F97316] text-white shadow-lg'
              : 'text-gray-400 hover:text-white hover:bg-[#2d3a56]'
          }`}
        >
          Market Order
        </button>
      </div>

      {orderType === 'market' && (
        <div className="bg-[#F97316]/10 border border-[#F97316]/30 rounded-lg p-3 text-sm">
          <div className="flex items-center gap-2 text-[#F97316] font-medium mb-1">
            <Activity className="h-4 w-4" />
            Market Order
          </div>
          <p className="text-gray-400 text-xs">
            {activeTab === 'buy'
              ? `Execute immediately at best ask price (${bestAsk}¢)`
              : `Execute immediately at best bid price (${bestBid}¢)`
            }
          </p>
          {noLiquidity && (
            <p className="text-red-400 text-xs mt-1">
              ⚠ No {activeTab === 'buy' ? 'sell' : 'buy'} orders available
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className={orderType === 'market' ? 'opacity-50 pointer-events-none' : ''}>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            {orderType === 'limit' ? 'Limit Price (¢)' : 'Market Price (¢)'}
          </label>
          <Input
            type="number"
            value={orderType === 'market' ? effectivePrice : price}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') {
                setPrice('');
              } else {
                const num = parseInt(val, 10);
                if (!isNaN(num)) {
                  setPrice(Math.min(99, Math.max(1, num)));
                }
              }
            }}
            onBlur={() => {
              if (price === '' || price < 1) setPrice(1);
              if (price > 99) setPrice(99);
            }}
            min={1}
            max={99}
            disabled={orderType === 'market'}
            data-testid="price-input"
            className="bg-[#1e2a45] border-[#2d3a56] text-white disabled:opacity-60"
          />
          {orderType === 'limit' && (
            <div className="flex gap-1 mt-1">
              {[25, 50, 75].map(p => (
                <button
                  key={p}
                  onClick={() => setPrice(p)}
                  className={`flex-1 py-1 text-xs rounded ${price === p ? 'bg-[#3B82F6] text-white' : 'bg-[#1e2a45] text-gray-400 hover:bg-[#2d3a56]'}`}
                >
                  {p}¢
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">Contracts</label>
          <Input
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
            min={1}
            data-testid="quantity-input"
            className="bg-[#1e2a45] border-[#2d3a56] text-white"
          />
          <div className="flex gap-1 mt-1">
            {[10, 25, 50, 100].map(q => (
              <button
                key={q}
                onClick={() => setQuantity(q)}
                className={`flex-1 py-1 text-xs rounded ${quantity === q ? 'bg-[#3B82F6] text-white' : 'bg-[#1e2a45] text-gray-400'}`}
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-[#1e2a45]/50 rounded-xl p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Order Type</span>
          <span className={`font-medium ${orderType === 'market' ? 'text-[#F97316]' : 'text-white'}`}>
            {orderType === 'market' ? 'MARKET' : 'LIMIT'} {activeTab.toUpperCase()} {quantity} {outcome} @ {formatCurrency(effectivePrice)}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">Total Cost</span>
          <span className="text-white font-semibold">{formatCurrency(totalCost)}</span>
        </div>
        {activeTab === 'buy' && (
          <>
            <div className="flex justify-between text-sm">
              <span className="text-gray-400">Potential Payout</span>
              <span className="text-green-400 font-semibold">{formatCurrency(potentialPayout)}</span>
            </div>
            <div className="flex justify-between text-sm border-t border-[#1e2a45] pt-2">
              <span className="text-gray-400">Max Profit</span>
              <span className="text-green-400 font-bold">+{formatCurrency(potentialProfit)}</span>
            </div>
          </>
        )}
        {isAuthenticated && (
          <div className="flex justify-between text-xs text-gray-500 pt-2">
            <span>Balance: ${(walletBalance / 100).toLocaleString()}</span>
            {userPositions.length > 0 && (
              <span>
                Position: {userPositions.find(p => p.outcome === outcome)?.shares || 0} {outcome}
              </span>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
          {error}
        </div>
      )}

      {showSubscribePrompt && (
        <div className="bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 bg-[#3B82F6]/20 rounded-lg">
              <Lock className="h-5 w-5 text-[#3B82F6]" />
            </div>
            <div>
              <h4 className="text-white font-semibold">Please Subscribe</h4>
              <p className="text-gray-400 text-sm">A subscription is required to buy and sell contracts</p>
            </div>
          </div>
          <Button
            onClick={() => {
              onCloseSubscribePrompt();
              if (onShowSubscribe) onShowSubscribe();
            }}
            className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white py-3 rounded-lg font-semibold"
            data-testid="subscribe-prompt-btn"
          >
            <CreditCard className="h-4 w-4 mr-2" />
            View Subscription Plans
          </Button>
        </div>
      )}

      <Button
        onClick={onPlaceOrder}
        disabled={submitting || (orderType === 'market' && noLiquidity)}
        data-testid="place-order-btn"
        className={`w-full py-6 rounded-full font-semibold ${
          orderType === 'market'
            ? 'bg-[#F97316] hover:bg-[#EA580C]'
            : activeTab === 'buy'
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-red-600 hover:bg-red-700'
        } text-white disabled:opacity-50`}
      >
        {submitting ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : !isAuthenticated ? (
          'Sign in to Trade'
        ) : orderType === 'market' ? (
          `${activeTab.toUpperCase()} ${quantity} ${outcome} @ Market`
        ) : (
          `${activeTab.toUpperCase()} ${quantity} ${outcome} @ ${price}¢`
        )}
      </Button>
    </>
  );
};
